import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import React from 'react';
import { ArrowLeftIcon, LinkExternalIcon, ScreenFullIcon } from '@primer/octicons-react';
import { LTActionMenu, LTTooltip } from '@lambdatestincprivate/lt-components';
import { browserDisplayName, formatDurationMs, osDisplayName, relativeTime, toBrowserKey, toOSKey, truncateMiddle } from '../../format';
import { BrowserIcon } from '../../icons/BrowserIcon';
import { CopyLinkButton } from '../../CopyLink/CopyLinkButton';
import { OSIcon } from '../../icons/OSIcon';
import { StatusPill } from './StatusPill';
import { TestListPopover } from './TestListPopover';
import styles from './header.module.css';
import { HYPEREXECUTE_BASE, TMS_CLIENT_BASE } from '../../../config';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
var NOT_IN_PACK = 'Not in evidence packs yet';

/** "Executed on Jul 7, 2026 at 4:17:27 PM" — kaneai's label tooltip. */
function executedTooltip(iso) {
  if (!iso) return undefined;
  var t = Date.parse(iso);
  if (Number.isNaN(t)) return undefined;
  var d = new Date(t);
  var date = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  }).format(d);
  var time = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true
  }).format(d);
  return "Executed on ".concat(date, " at ").concat(time);
}
function Column(_ref) {
  var label = _ref.label,
    labelTooltip = _ref.labelTooltip,
    children = _ref.children;
  return /*#__PURE__*/_jsxs("div", {
    className: styles.column,
    children: [/*#__PURE__*/_jsx("span", {
      className: styles.columnLabel,
      title: labelTooltip,
      children: label
    }), children]
  });
}
var ConfigDot = function ConfigDot() {
  return /*#__PURE__*/_jsx("span", {
    className: styles.configDot,
    "aria-hidden": "true"
  });
};
var capitalize = function capitalize(s) {
  return s.length > 0 ? s.charAt(0).toUpperCase() + s.slice(1) : s;
};

/**
 * Retry switcher — only rendered when the pack actually contains numbered
 * attempt mirrors (tests/<id>/<n>/). Labels are chronological "Attempt n";
 * statuses come from result.yaml's authored `attempts:` list when present
 * (never derived). The LAST attempt is the top-level (final) run.
 */
function AttemptsMenu(_ref2) {
  var _statusOf2;
  var attempt = _ref2.attempt,
    retryDirs = _ref2.retryDirs,
    attemptsMeta = _ref2.attemptsMeta,
    finalStatus = _ref2.finalStatus,
    onSelectAttempt = _ref2.onSelectAttempt;
  var total = attemptsMeta && attemptsMeta.length > 0 ? attemptsMeta.length : retryDirs[retryDirs.length - 1] + 1;
  var statusOf = function statusOf(n) {
    // attemptsMeta is chronological & 1-based-aligned: index n-1 = attempt n.
    var meta = attemptsMeta === null || attemptsMeta === void 0 ? void 0 : attemptsMeta[n - 1];
    return meta ? meta.status : null;
  };
  var itemName = function itemName(n, isFinal) {
    var _statusOf;
    var status = isFinal ? (_statusOf = statusOf(n)) !== null && _statusOf !== void 0 ? _statusOf : finalStatus : statusOf(n);
    var base = "Attempt ".concat(n);
    var suffix = status ? " \u2014 ".concat(capitalize(status)) : '';
    return isFinal ? "".concat(base).concat(suffix, " \xB7 Final") : "".concat(base).concat(suffix);
  };
  // Trigger names the attempt ON SCREEN (user: "so that we know what is
  // on UI"); the retry COUNT lives in the metadata columns instead.
  var currentN = attempt > 0 ? attempt : total;
  var currentStatus = attempt > 0 ? statusOf(attempt) : (_statusOf2 = statusOf(total)) !== null && _statusOf2 !== void 0 ? _statusOf2 : finalStatus;
  var buttonLabel = "Attempt ".concat(currentN).concat(currentStatus ? " : ".concat(capitalize(currentStatus)) : '');
  return /*#__PURE__*/_jsx(LTActionMenu, {
    buttonLabel: buttonLabel,
    anchorButtonSize: "medium"
    // Portals mount outside the viewer root — restate the font.
    ,
    overlayStyle: {
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Noto Sans', Helvetica, Arial, sans-serif"
    },
    options: [{
      title: '',
      list: [].concat(_toConsumableArray(retryDirs.map(function (n) {
        return {
          id: "attempt-".concat(n),
          name: itemName(n, false),
          isSelected: attempt === n,
          onClickHandler: function onClickHandler() {
            return onSelectAttempt(n);
          }
        };
      })), [{
        id: 'attempt-final',
        name: itemName(total, true),
        isSelected: attempt === 0,
        onClickHandler: function onClickHandler() {
          return onSelectAttempt(0);
        }
      }])
    }]
  });
}

/**
 * Port of kaneai's compact TestDetailTopHeader (LTTestHeader compact branch):
 * one row — status pill · labeled columns · actions far right — over an
 * LTDivider. The test name is deliberately absent (kaneai's compact header
 * omits it too); an sr-only h1 keeps the page outline + focus target.
 * Deviation (flagged): a back button leads the row — the standalone viewer
 * has no breadcrumb to navigate back with.
 */
export function CompactHeader(_ref3) {
  var _retryDirs$length, _header$tcId, _header$tcId2, _executedTooltip, _header$executedBy$em;
  var header = _ref3.header,
    titleRef = _ref3.titleRef,
    onBack = _ref3.onBack,
    onEnterFullscreen = _ref3.onEnterFullscreen,
    _ref3$attempt = _ref3.attempt,
    attempt = _ref3$attempt === void 0 ? 0 : _ref3$attempt,
    _ref3$retryDirs = _ref3.retryDirs,
    retryDirs = _ref3$retryDirs === void 0 ? null : _ref3$retryDirs,
    attemptsMeta = _ref3.attemptsMeta,
    onSelectAttempt = _ref3.onSelectAttempt;
  var os = toOSKey(header.env.os);
  var browser = toBrowserKey(header.env.browser);
  var executed = relativeTime(header.startedAt);
  var retryCount = attemptsMeta && attemptsMeta.length > 1 ? attemptsMeta.length - 1 : (_retryDirs$length = retryDirs === null || retryDirs === void 0 ? void 0 : retryDirs.length) !== null && _retryDirs$length !== void 0 ? _retryDirs$length : 0;
  var configItems = [];
  if (header.env.browser) {
    var _header$env$browserVe;
    configItems.push(/*#__PURE__*/_jsxs("li", {
      title: browserDisplayName(browser),
      children: [/*#__PURE__*/_jsxs("span", {
        className: "ev-sr-only",
        children: [browserDisplayName(browser), ": "]
      }), /*#__PURE__*/_jsx(BrowserIcon, {
        browser: browser,
        size: 16
      }), /*#__PURE__*/_jsx("span", {
        className: styles.columnValue,
        children: (_header$env$browserVe = header.env.browserVersion) !== null && _header$env$browserVe !== void 0 ? _header$env$browserVe : header.env.browser
      })]
    }, "browser"));
  }
  if (header.env.os) {
    configItems.push(/*#__PURE__*/_jsxs("li", {
      title: osDisplayName(os),
      children: [/*#__PURE__*/_jsxs("span", {
        className: "ev-sr-only",
        children: [osDisplayName(os), ": "]
      }), /*#__PURE__*/_jsx(OSIcon, {
        os: os,
        size: 16
      }), /*#__PURE__*/_jsx("span", {
        className: styles.columnValue,
        children: header.env.osVersion ? "".concat(header.env.os, " ").concat(header.env.osVersion) : header.env.os
      })]
    }, "os"));
  }
  if (header.env.resolution) {
    configItems.push(/*#__PURE__*/_jsxs("li", {
      title: "Resolution",
      children: [/*#__PURE__*/_jsx("span", {
        className: "ev-sr-only",
        children: "Resolution: "
      }), /*#__PURE__*/_jsx("span", {
        className: styles.columnValue,
        children: header.env.resolution
      })]
    }, "resolution"));
  }

  // Primary TC-id deep link: project + test-case id -> the TMS test-case
  // dashboard (TMS handles login -> redirect-back when opened signed-out).
  // Older packs without those ids fall back to the legacy sharable_link.
  var testCaseHref = header.projectId && header.testcaseId ? "".concat(TMS_CLIENT_BASE, "/projects/").concat(header.projectId, "/test-cases/").concat(header.testcaseId, "/dashboard") : header.sharableLink;

  // "View on HyperExecute" CTA — external_id.hyper_job_id -> the task page.
  var hyperExecuteHref = header.hyperJobId ? "".concat(HYPEREXECUTE_BASE, "/hyperexecute/task?jobId=").concat(header.hyperJobId) : null;
  return /*#__PURE__*/_jsxs("div", {
    className: styles.headerBlock,
    children: [/*#__PURE__*/_jsx("h1", {
      className: "ev-sr-only",
      tabIndex: -1,
      ref: titleRef,
      children: header.title
    }), /*#__PURE__*/_jsxs("div", {
      className: styles.compactRow,
      children: [/*#__PURE__*/_jsx("button", {
        type: "button",
        className: "".concat(styles.iconButton, " ").concat(styles.iconButtonDefault),
        "aria-label": "Back to all test instances",
        onClick: onBack,
        children: /*#__PURE__*/_jsx(ArrowLeftIcon, {
          size: 16,
          "aria-hidden": "true"
        })
      }), /*#__PURE__*/_jsx(StatusPill, {
        status: header.status
      }), /*#__PURE__*/_jsxs("div", {
        className: styles.columns,
        children: [header.durationMs !== null && /*#__PURE__*/_jsx(Column, {
          label: "Duration",
          children: /*#__PURE__*/_jsx("span", {
            className: styles.columnValue,
            children: formatDurationMs(header.durationMs)
          })
        }), configItems.length > 0 && /*#__PURE__*/_jsx(Column, {
          label: "Configurations",
          children: /*#__PURE__*/_jsx("ul", {
            className: styles.configList,
            children: configItems.map(function (item, i) {
              return /*#__PURE__*/_jsxs(React.Fragment, {
                children: [i > 0 && /*#__PURE__*/_jsx("li", {
                  "aria-hidden": "true",
                  children: /*#__PURE__*/_jsx(ConfigDot, {})
                }), item]
              }, i);
            })
          })
        }), header.testcaseId && /*#__PURE__*/_jsx(Column, {
          label: "Test Case ID",
          children: /*#__PURE__*/_jsx(LTTooltip, {
            text: header.testcaseId,
            direction: "bottom",
            customTriggerStyle: {
              display: 'inline-flex'
            },
            children: testCaseHref ? /*#__PURE__*/_jsx("a", {
              className: styles.tcLink,
              href: testCaseHref,
              target: "_blank",
              rel: "noopener noreferrer",
              children: (_header$tcId = header.tcId) !== null && _header$tcId !== void 0 ? _header$tcId : truncateMiddle(header.testcaseId, 15)
            }) : /*#__PURE__*/_jsx("span", {
              className: styles.columnValue,
              children: (_header$tcId2 = header.tcId) !== null && _header$tcId2 !== void 0 ? _header$tcId2 : truncateMiddle(header.testcaseId, 15)
            })
          })
        }), /*#__PURE__*/_jsx(Column, {
          label: "Version",
          labelTooltip: NOT_IN_PACK,
          children: /*#__PURE__*/_jsx("span", {
            className: styles.columnValue,
            title: NOT_IN_PACK,
            children: "\u2014"
          })
        }), /*#__PURE__*/_jsx(Column, {
          label: executed ? "Executed ".concat(executed, " by") : 'Executed by',
          labelTooltip: (_executedTooltip = executedTooltip(header.startedAt)) !== null && _executedTooltip !== void 0 ? _executedTooltip : NOT_IN_PACK,
          children: header.executedBy ? /*#__PURE__*/_jsx("span", {
            className: styles.columnValue,
            title: (_header$executedBy$em = header.executedBy.email) !== null && _header$executedBy$em !== void 0 ? _header$executedBy$em : undefined,
            children: truncateMiddle(header.executedBy.name, 24)
          }) : /*#__PURE__*/_jsx("span", {
            className: styles.columnValue,
            title: NOT_IN_PACK,
            children: "\u2014"
          })
        }), /*#__PURE__*/_jsx(Column, {
          label: "Labels",
          labelTooltip: header.tags.length > 0 ? undefined : NOT_IN_PACK,
          children: header.tags.length > 0 ? /*#__PURE__*/_jsxs("span", {
            className: styles.columnValue,
            title: header.tags.join(', '),
            children: [header.tags.slice(0, 3).join(', '), header.tags.length > 3 ? " +".concat(header.tags.length - 3) : '']
          }) : /*#__PURE__*/_jsx("span", {
            className: styles.columnValue,
            title: NOT_IN_PACK,
            children: "\u2014"
          })
        }), retryCount > 0 && /*#__PURE__*/_jsx(Column, {
          label: "Retries",
          children: /*#__PURE__*/_jsx("span", {
            className: styles.columnValue,
            children: retryCount
          })
        })]
      }), /*#__PURE__*/_jsxs("div", {
        className: styles.actions,
        children: [retryDirs !== null && retryDirs.length > 0 && onSelectAttempt && /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx(AttemptsMenu, {
            attempt: attempt,
            retryDirs: retryDirs,
            attemptsMeta: attemptsMeta,
            finalStatus: header.status,
            onSelectAttempt: onSelectAttempt
          }), /*#__PURE__*/_jsx("div", {
            className: styles.actionsDivider,
            "aria-hidden": "true"
          })]
        }), hyperExecuteHref && /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsxs("a", {
            className: styles.hyperExecuteCta,
            href: hyperExecuteHref,
            target: "_blank",
            rel: "noopener noreferrer",
            children: [/*#__PURE__*/_jsx(LinkExternalIcon, {
              size: 14,
              "aria-hidden": "true"
            }), /*#__PURE__*/_jsx("span", {
              children: "View on HyperExecute"
            })]
          }), /*#__PURE__*/_jsx("div", {
            className: styles.actionsDivider,
            "aria-hidden": "true"
          })]
        }), /*#__PURE__*/_jsx(CopyLinkButton, {
          className: "".concat(styles.iconButton, " ").concat(styles.iconButtonInvisible)
        }), /*#__PURE__*/_jsx("button", {
          type: "button",
          className: "".concat(styles.iconButton, " ").concat(styles.iconButtonInvisible),
          "aria-label": "Toggle fullscreen",
          onClick: onEnterFullscreen,
          children: /*#__PURE__*/_jsx(ScreenFullIcon, {
            size: 16,
            "aria-hidden": "true"
          })
        }), /*#__PURE__*/_jsx("div", {
          className: styles.actionsDivider,
          "aria-hidden": "true"
        }), /*#__PURE__*/_jsx(TestListPopover, {
          currentId: header.testId
        })]
      })]
    }), /*#__PURE__*/_jsx("div", {
      className: styles.pageDivider,
      "aria-hidden": "true"
    })]
  });
}